import { useId, type SVGProps } from 'react';

/** Flat UI glyphs drawn in code. Painted art (buttons, characters) comes from assets instead. */
export type IconName =
  'speaker' | 'help' | 'star' | 'starEmpty' | 'sparkle' | 'globe' | 'lock' | 'close';

interface IconProps extends SVGProps<SVGSVGElement> {
  name: IconName;
}

export function Icon({ name, ...rest }: IconProps) {
  const gradientId = useId();
  return (
    <svg viewBox="0 0 24 24" aria-hidden focusable="false" {...rest}>
      {name === 'speaker' && (
        <>
          <path d="M3 9.5h3.6L11.5 5v14l-4.9-4.5H3z" fill="currentColor" />
          <path
            d="M15 9a4 4 0 0 1 0 6M17.8 6.4a7.6 7.6 0 0 1 0 11.2"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </>
      )}
      {name === 'help' && (
        <>
          {/* A light bulb: "show me how". */}
          <path
            d="M12 3.2a6 6 0 0 0-3.6 10.8c.7.5 1.1 1.3 1.1 2.1v.6h5v-.6c0-.8.4-1.6 1.1-2.1A6 6 0 0 0 12 3.2z"
            fill="currentColor"
          />
          <path
            d="M9.8 19h4.4M10.6 21.2h2.8"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
          />
        </>
      )}
      {name === 'star' && (
        <>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#fff06b" />
              <stop offset="1" stopColor="#ffae00" />
            </linearGradient>
          </defs>
          <path
            d="M12 2.6l2.8 5.9 6.4.8-4.7 4.4 1.2 6.4L12 17l-5.7 3.1 1.2-6.4L2.8 9.3l6.4-.8z"
            fill={`url(#${gradientId})`}
            stroke="#8a4b00"
            strokeWidth="1.3"
            strokeLinejoin="round"
          />
        </>
      )}
      {name === 'starEmpty' && (
        <path
          d="M12 2.6l2.8 5.9 6.4.8-4.7 4.4 1.2 6.4L12 17l-5.7 3.1 1.2-6.4L2.8 9.3l6.4-.8z"
          fill="rgb(10 20 50 / 0.55)"
          stroke="rgb(140 170 220 / 0.6)"
          strokeWidth="1.3"
          strokeLinejoin="round"
        />
      )}
      {name === 'close' && (
        <path
          d="M6.5 6.5l11 11M17.5 6.5l-11 11"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.6"
          strokeLinecap="round"
        />
      )}
      {name === 'lock' && (
        <>
          <path
            d="M7.5 10.5V8a4.5 4.5 0 0 1 9 0v2.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
          />
          <rect x="5" y="10.5" width="14" height="10.5" rx="2.5" fill="currentColor" />
          <circle cx="12" cy="15.3" r="1.6" fill="rgb(0 0 0 / 0.45)" />
        </>
      )}
      {name === 'globe' && (
        <g fill="none" stroke="currentColor" strokeWidth="1.6">
          <circle cx="12" cy="12" r="9" />
          <ellipse cx="12" cy="12" rx="4" ry="9" />
          <path d="M3 12h18M5 7.5h14M5 16.5h14" />
        </g>
      )}
      {name === 'sparkle' && (
        <path
          d="M12 1.5c.9 5.6 4.9 9.6 10.5 10.5-5.6.9-9.6 4.9-10.5 10.5C11.1 16.9 7.1 12.9 1.5 12 7.1 11.1 11.1 7.1 12 1.5z"
          fill="currentColor"
        />
      )}
    </svg>
  );
}
